import React, { useMemo, useState } from 'react';

import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { Animation, Checkbox } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import { EMPTY_OBJECT } from '../../constants';

import styles from './providerFilterGroup.module.scss';

import type { Groups } from '../../types/providerList';

export type Props = {
  className?: string;
  titleClassName?: string;
  optionTextClassName?: string;
  showMoreButtonClassName?: string;
  /** String of id to identify which filter group is updated */
  id: string;
  /** String of filter title */
  title?: React.ReactNode;
  /** Name of filter group */
  name?: string;
  /** Array of filter options */
  options: Array<Groups & { className?: string; iconClassName?: string; isDisabled?: boolean }>;
  /** Callback to be called when filter has been updated */
  onChange?: (groupId: string, featureId: string) => void;
  /** Callback to be called when filter has been updated */
  type?: 'single' | 'multiple';
  /** Node to be displayed when list is minimized */
  showAllLabel?: React.ReactNode;
  /** Node to be displayed when list is maximized */
  showLessLabel?: React.ReactNode;
  /** Number of max visible items on first displayed */
  maxVisibleCount?: number;
  /** boolean of display hits on each filter group */
  shouldDisplayHits?: boolean;
};

function ProviderFilterGroup(props: Props) {
  const {
    id,
    title,
    options,
    name,
    type,
    showAllLabel,
    showLessLabel,
    titleClassName,
    optionTextClassName,
    maxVisibleCount,
    shouldDisplayHits,
    showMoreButtonClassName,
  } = props;
  const deviceType = useDeviceType();

  const [isShowAll, setIsShowAll] = useState(false);

  const validOptions = useMemo(
    () => options.filter((option) => option.name && option.name.trim().length !== 0),
    [options],
  );

  const MAX_VISIBLE = maxVisibleCount ? maxVisibleCount : deviceType === MOBILE_SCREEN ? 3 : 6;

  const visibleOptions = validOptions.slice(0, MAX_VISIBLE);
  const invisibleOptions = validOptions.slice(MAX_VISIBLE, validOptions.length);
  const customStyles =
    type === 'single'
      ? {
          iconClassName: styles.optionIcon,
          checkedClassName: styles.checked,
          customCheckMarkIcon: ' ',
        }
      : EMPTY_OBJECT;

  const renderCheckbox = (option: (typeof options)[number]) => {
    const handleChange = (featureId: string) => {
      props.onChange?.(id, featureId);
    };

    const checkboxComponent = (
      <Checkbox
        id={`filter-option-${option.featureId}`}
        className={cx(styles.optionText, optionTextClassName, option.className)}
        iconClassName={option.iconClassName}
        {...customStyles}
        label={option.name}
        isChecked={option.selected}
        isDisabled={option.isDisabled}
        onChange={() => handleChange(option.featureId)}
        // NOTE: Dont pass name here since there will be additional param on URL when submitting the form
        name=""
      />
    );

    const renderComponent = (
      <>
        {checkboxComponent}
        {isNotEmptyArray(option.secondFeatures) &&
          option.secondFeatures.map((item) => (
            <Checkbox
              id={`filter-option-${item.featureId}`}
              className={cx(styles.optionText, optionTextClassName, item.className)}
              iconClassName={option.iconClassName}
              {...customStyles}
              label={item.name}
              isChecked={item.selected}
              onChange={() => handleChange(item.featureId)}
              // NOTE: Dont pass name here since there will be additional param on URL when submitting the form
              name=""
            />
          ))}
      </>
    );

    return shouldDisplayHits ? (
      <div key={option.featureId} className={styles.checkboxWrapper}>
        {checkboxComponent}
        {option.hits}
      </div>
    ) : (
      renderComponent
    );
  };

  return (
    <fieldset
      data-testid="ProviderFilterGroup-wrapper"
      className={cx(props.className, styles.conditionContainer)}
      id={`ProviderFilterGroup-${id}`}
    >
      {title && <legend className={cx(styles.filterGroupName, titleClassName)}>{title}</legend>}
      {visibleOptions.map(renderCheckbox)}
      <Animation
        isStartAction={isShowAll}
        type="fadeInDown"
        animationDuration="short"
        wrapperClassName={styles.moreConditionsBox}
      >
        {invisibleOptions.map(renderCheckbox)}
      </Animation>
      {validOptions.length > MAX_VISIBLE && (
        <button
          className={cx(styles.showMoreButton, showMoreButtonClassName)}
          data-testid={`showMoreButton-${name ? `${name}` : `noName`}-div`}
          onClick={() => setIsShowAll((prev) => !prev)}
          type="button"
        >
          {isShowAll ? showLessLabel : showAllLabel}
        </button>
      )}
    </fieldset>
  );
}

export default ProviderFilterGroup;
